/**
 * "Practising out loud" — the one thing this device has to be asked about.
 *
 * The speaking exercises can listen and score what a child said, but only where
 * the audio provably never leaves the device
 * ([D-095](../../../docs/DECISIONS.md#d-095)). On Chrome that means an
 * on-device model, and the model is a **download onto somebody's device** —
 * which is a grown-up's decision, not something to do quietly at launch while a
 * nine-year-old is waiting for a lesson
 * ([D-096](../../../docs/DECISIONS.md#d-096)).
 *
 * So it lives on the grown-ups screen, next to the other device-level things,
 * and it says what it is for in the order a parent cares about: what it gets
 * you, what it costs, and where the recording goes.
 *
 * **Nothing here is required.** Every speaking exercise already works without
 * it — a child taps *I said it* and is taken at their word — so this screen is
 * an offer and never a warning.
 */

import { useCallback, useEffect, useState } from 'react';
import { recognitionTag } from './language.js';
import { type SpeechState, installSpeech, speechState } from './speech.js';

/**
 * One card per language the family's children learn (D-190): the on-device
 * model is per language, so Swedish needs its own download.
 */
export default function SpeechSetup({
  l2 = 'en',
  title = 'English',
  named = false,
}: {
  /** The language to listen for. */
  readonly l2?: string;
  readonly title?: string;
  /** Say which language in the heading, when there is more than one card. */
  readonly named?: boolean;
} = {}) {
  const lang = recognitionTag(l2);
  const [state, setState] = useState<SpeechState | null>(null);
  const [working, setWorking] = useState(false);
  const [failed, setFailed] = useState(false);

  const refresh = useCallback(async (): Promise<SpeechState> => {
    const next = await speechState(lang);
    setState(next);
    return next;
  }, [lang]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const fetchModel = useCallback(() => {
    setWorking(true);
    setFailed(false);
    // Kicked off from the tap itself, because Chrome will only start the
    // download while a real click is being handled.
    void installSpeech(lang)
      .then(() => refresh())
      .then((next) => {
        setWorking(false);
        // `install` resolving is not the same as the model being there — it
        // answers "did it start", and a browser can decline quietly. The state
        // afterwards is the truth.
        setFailed(next !== 'available' && next !== 'downloading');
      })
      .catch(() => {
        setWorking(false);
        setFailed(true);
      });
  }, [refresh, lang]);

  // Nothing is known yet. Silent rather than a spinner: this is a card in a
  // list of settings, not a screen a grown-up is waiting on.
  if (state === null) return null;

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <h3>{named ? `Practising ${title} out loud` : 'Practising out loud'}</h3>

      {state === 'unsupported' && (
        <p className="tiny" style={{ marginTop: 4 }}>
          This browser cannot listen without sending the recording to someone else’s
          computer, so CapyTalk will not ask for the microphone here. The speaking
          exercises still work — your child reads the words aloud and taps{' '}
          <b>I said it</b>. Chrome on a computer can do it properly.
        </p>
      )}

      {state === 'downloadable' && (
        <>
          <p className="tiny" style={{ marginTop: 4 }}>
            CapyTalk can listen to your child speaking and use it to pick what to
            practise next — but only if the listening happens <b>on this device</b>.
            That needs a one-off download of {title} speech recognition.
          </p>
          <p className="tiny" style={{ marginTop: 6 }}>
            The recording never leaves this device and is never saved. Nothing is sent
            to CapyTalk’s server except how it went. It is entirely optional: without it
            your child taps <b>I said it</b>, exactly as they do now.
          </p>
          <button
            className="btn"
            type="button"
            style={{ marginTop: 10 }}
            disabled={working}
            onClick={fetchModel}
          >
            {working ? 'Downloading…' : 'Download it'}
          </button>
          {failed && (
            <p className="tiny" role="status" style={{ marginTop: 8 }}>
              That did not start. It may need a better connection, or more room on the
              device. Nothing is broken — speaking works without it.
            </p>
          )}
        </>
      )}

      {state === 'downloading' && (
        <p className="tiny" role="status" style={{ marginTop: 4 }}>
          The speech recognition is downloading. It will start working on its own when
          it is done — there is nothing to wait for here.
        </p>
      )}

      {state === 'available' && (
        <p className="tiny" style={{ marginTop: 4 }}>
          Ready. Your child can tap <b>🎤 Listen to me</b> in the speaking exercises,
          and the recording stays on this device. They can still tap <b>I said it</b>{' '}
          instead whenever they would rather not speak.
        </p>
      )}
    </div>
  );
}
